import { HULL_HALF_WIDTH, HULL_HEIGHT_DUCK, HULL_HEIGHT_STAND } from './constants';
import type { SolidEntity } from './collision/types';
import type { Vec3 } from './vec';

export interface SpawnPoint {
  pos: Vec3;
  yaw: number;
  stage: number;
}

export const HULL_MINS: Readonly<Vec3> = [-HULL_HALF_WIDTH, -HULL_HALF_WIDTH, 0];
export const HULL_MAXS_STAND: Readonly<Vec3> = [HULL_HALF_WIDTH, HULL_HALF_WIDTH, HULL_HEIGHT_STAND];
export const HULL_MAXS_DUCK: Readonly<Vec3> = [HULL_HALF_WIDTH, HULL_HALF_WIDTH, HULL_HEIGHT_DUCK];

/**
 * Presentation outbox: written by the simulation, read and cleared by the render layer.
 * The simulation never reads these back, so clearing them can't affect movement.
 */
export interface PlayerFx {
  /** Instant eye-height change since last frame (air duck keeps the head still). */
  eyeShift: number;
  /** If >= 0, the eye height was reset to this value (teleport) before eyeShift accumulated. */
  eyeSnap: number;
  /** Strongest landing (downward speed) since last frame, for the viewmodel dip. */
  landImpact: number;
  /** Set when the view angles must be snapped (teleport). The client applies it to its view. */
  snapYaw: number | null;
}

export interface PlayerState {
  pos: Vec3;
  /** Position at the start of the last tick, for render interpolation. */
  prev: Vec3;
  vel: Vec3;
  ground: SolidEntity | null;
  ducked: boolean;
  /** Jump button state last tick (for legit jumping). */
  oldJump: boolean;
  /** Yaw of the previous tick, for strafe sync. */
  lastYaw: number;
  /** Horizontal speed at the moment of the last jump. */
  jumpSpeed: number;
  /** Downward speed of the last landing (for sounds). */
  landSpeed: number;
  /**
   * Source "base velocity" from push triggers: added to velocity while moving, turned into real
   * momentum when the player leaves the push. Z is applied as acceleration (StartGravity).
   */
  baseVel: Vec3;
  /** Set by a push trigger this tick (FL_BASEVELOCITY). */
  baseVelActive: boolean;
  /** Gravity multiplier (trigger_gravity), 1 = normal. */
  gravity: number;
  /** On a ladder (Source MOVETYPE_LADDER), and the ladder surface normal. */
  ladder: boolean;
  ladderNormal: Vec3;
  /** player_speedmod (Source lagged movement): movement runs this many times as fast. */
  laggedMovement: number;
  /** Fractional moves owed by laggedMovement. */
  lagAcc: number;
  /** Sliding down a slope after a fall (cv.slopeSlide). */
  sliding: boolean;
  /** 0 dry, 1 feet, 2 waist, 3 eyes in water. */
  waterLevel: number;
  /** Leaping out of water (Source m_flWaterJumpTime, ms) and its sideways velocity. */
  waterJumpTime: number;
  waterJumpVel: [number, number];
  fx: PlayerFx;
}

export function createPlayer(): PlayerState {
  return {
    pos: [0, 0, 0],
    prev: [0, 0, 0],
    vel: [0, 0, 0],
    ground: null,
    ducked: false,
    oldJump: false,
    lastYaw: 0,
    jumpSpeed: 0,
    landSpeed: 0,
    baseVel: [0, 0, 0],
    baseVelActive: false,
    gravity: 1,
    ladder: false,
    ladderNormal: [0, 0, 0],
    sliding: false,
    laggedMovement: 1,
    lagAcc: 0,
    waterLevel: 0,
    waterJumpTime: 0,
    waterJumpVel: [0, 0],
    fx: { eyeShift: 0, eyeSnap: -1, landImpact: 0, snapYaw: null },
  };
}

export const hullMaxs = (p: PlayerState): Readonly<Vec3> => (p.ducked ? HULL_MAXS_DUCK : HULL_MAXS_STAND);
export const hullHeight = (p: PlayerState): number => (p.ducked ? HULL_HEIGHT_DUCK : HULL_HEIGHT_STAND);
export const horizontalSpeed = (p: PlayerState): number => Math.hypot(p.vel[0], p.vel[1]);
